// =============================================================================
// app.js — Global entry point for simplybook.me (public site)
//
// Loaded on every public page via encore_entry_script_tags('app') in main.html.twig.
// Provides: jQuery ($), global singletons (Login, Feedback, _t, rudderstack),
// core UI components, analytics, and page-level interaction handlers.
//
// Per-page JS lives in assets/js/pages/. Heavy modules are lazy-loaded via
// assets/js/components/lazyInitializer.js (slick-slider, etc.)
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS — side-effect (no export used)
// ─────────────────────────────────────────────────────────────────────────────

require('./components/ui-components/modal-bootstrap-v3.4.1');
require('./components/ui-components/animateOnScoroll');
require('./components/ui-components/scrollSnapArm');

// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS — named modules
// ─────────────────────────────────────────────────────────────────────────────

const $ = require("jquery");

import { NotificationQueue } from "./components/ui-components/notificationQueue";
import { initEagerComponents, initLazyComponents } from './components/lazyInitializer';

const TooltipPositioning  = require('./components/ui-components/tooltip.js').TooltipPositioning;
const lazyImages          = require('./components/ui-components/LazyLoad.js').lazyImages;
const videoPopup          = require('./components/ui-components/VideoPopup').videoPopup;
const copyPrompt = require('./components/ui-components/copyPrompt').copyPrompt;

const { getMode } = require('./media.js');
const StickyHeader    = require('./StickyHeader.js').StickyHeader;
const StickyTabs      = require('./StickyHeader.js').StickyTabs;
const FooterAccordion = require('./FooterAccordion.js').FooterAccordion;
const Icons           = require('./Icons.js').Icons;
const effects         = require('./effects.js');
const { debugMode }   = require('./debug-mode.js');
const support         = require('./support.js');

const Login                    = require('./login/login').Login;
const { initSimpleTabs }       = require("./components/ui-components/tab");
const rudderstack              = require('./analytics/rudderstack/rudderstack.js').rudderstack;
const { initRudderstackAnalytics } = require('./analytics/rudderstack/events.js');


// ─────────────────────────────────────────────────────────────────────────────
// GLOBALS — singletons exposed to Twig-rendered inline scripts and page JS
// ─────────────────────────────────────────────────────────────────────────────

global.Login     = Login;
global.Feedback  = require('./feedback/feedback').Feedback;
global._t        = _t;        // translation helper — defined below (hoisted)
global.rudderstack = rudderstack;
// jQuery's dist build suppresses window.$/window.jQuery when bundled (CommonJS noGlobal
// guard) — restore it, since inline <script> blocks in Twig layouts rely on the global.
global.$ = global.jQuery = $;

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs callback during browser idle time (or after `timeout` ms fallback).
 * Used to defer non-critical work past LCP/TBT measurement window.
 */
function runWhenIdle(cb, timeout) {
	var t = (typeof timeout === 'number') ? timeout : 1200;
	if (window.requestIdleCallback) window.requestIdleCallback(function () { cb(); }, { timeout: t });
	else setTimeout(cb, 1);
}

/**
 * Waits until `selector` appears in DOM, then runs `callback` once.
 * Works for SSR, delayed Twig includes, React mounts, etc.
 */
function runWhenReady(selector, callback, options = {}) {
	const { once = true, maxWait = 4000 } = options;
	if (document.querySelector(selector)) { callback(); return; }

	let done = false;
	const observer = new MutationObserver(() => {
		if (done) return;
		if (document.querySelector(selector)) {
			if (once) done = true;
			observer.disconnect();
			callback();
		}
	});
	observer.observe(document.body, { childList: true, subtree: true });

	setTimeout(() => {
		if (!done && document.querySelector(selector)) { callback(); observer.disconnect(); }
	}, maxWait);
}

/**
 * Runs `fn` after DOMContentLoaded, or immediately if DOM is already ready.
 */
function onDOMReady(fn) {
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
	else fn();
}

/**
 * Global translation helper. Looks up `key` in the LANG object injected by Symfony.
 * Called as window._t('key') from inline scripts or Twig-rendered JS.
 */
function _t(key, options) {
	let str = key;
	if (typeof LANG !== 'undefined' && LANG[key]) {
		str = LANG[key];
	} else {
		console.warn('Lang key missing', key);
	}
	if (options !== undefined) {
		for (let k in options) {
			if (options.hasOwnProperty(k)) str = (str + '').replace('%' + k + '%', options[k]);
		}
	}
	return str;
}

// ─────────────────────────────────────────────────────────────────────────────
// CORE UI — immediate initialization on every page
// ─────────────────────────────────────────────────────────────────────────────

new StickyHeader();
new StickyTabs();
new FooterAccordion();
new Icons();

// @deprecated — moved to slick-slider.js preset system via lazyInitializer
// new TemplatesSlider();
// new ReviewsSlider();
// new ReviewsSlider_v2();
// new moreBookingsCarousel();

// Handle lazy loading images
lazyImages();
// Handle lazy loading youtube video in popups
videoPopup();
// Copy-to-clipboard for the Ask Simply starter prompts
copyPrompt();

effects.addRippleToButton($('.btn, li.to-previous-step, .features-sidebar__category-item, .features-list__item-details'));

// Dev/perf/SEO dashboard — silent unless debugMode (yarn encore dev, or
// ?debug=1 / localStorage.sb_debug=1 on any build). Deferred to idle time,
// doesn't need to run before LCP/TBT are measured. Loaded with import() so
// regular visitors don't download it. See assets/js/debug.js.
if (debugMode) {
    runWhenIdle(function () {
        import(/* webpackChunkName: "debug" */ './debug.js').then(function (debug) { debug.runDashboard(); });
    });
}

// Eager UI components (accordion, tabs, features-list) — already in the main
// bundle, so initializing synchronously here costs zero extra network request
// and avoids the IntersectionObserver lazy-init race with beforematch/Find-in-Page.
initEagerComponents();

// Lazy-load heavy components on demand (slick-slider, etc.) — see lazyInitializer.js
initLazyComponents();

support.supportChat();
new TooltipPositioning();

// Cookie banner is authoritative for the support chat: unlock the queue as soon
// as iubenda has actually resolved consent (see the onConsentGiven callback wired in
// iubenda-cookies.html.twig), or immediately if the iubenda scripts failed to load (see
// the onerror-driven window.__onCookieScriptLoadError in that same template — real
// failure detection, not a guess). This 30s timer is only a last-resort backstop for the
// rare case neither of those ever fires; it's deliberately long so a real, still-active
// cookie banner isn't buried under the chat card just because the user hasn't clicked yet.
if (window.__cookieConsentResolved) {
	NotificationQueue.unlock();
} else {
	setTimeout(() => NotificationQueue.unlock(), 30000);
}

// QUIZ APP is NOT initialized here: it has its own `quizApp` entry
// (assets/js/pages/quizApp/main.js), loaded only by the templates that include
// enterprise-landing/routing-form. Initializing it here too creates a second
// instance on those pages, which doubles every listener, so one booking fires
// the Google Ads "bookingDone" conversion twice.

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS — RudderStack event tracking
// ─────────────────────────────────────────────────────────────────────────────

initRudderstackAnalytics();

// ─────────────────────────────────────────────────────────────────────────────
// PAGE-READY — deferred CSS class after LCP/TBT window
//
// Adds `is-page-ready` to <body> 500ms after window.load.
// Used by SCSS to enable backdrop-filter / GPU compositing layers without
// affecting Lighthouse Core Web Vitals measurements.
// ─────────────────────────────────────────────────────────────────────────────

window.addEventListener('load', function () {
	setTimeout(function () { document.body.classList.add('is-page-ready'); }, 500);
}, { once: true, passive: true });

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE NAVIGATION — header menu toggle
// ─────────────────────────────────────────────────────────────────────────────

var menuWasOpen = false;

$('.header--togg-menu').click(function () {
	menuWasOpen = !menuWasOpen;
	closeMenu();
});

$('.morph-shape, .header--mob-togg-menu, .sb-header--profile-toggler').click(function () {
    menuWasOpen = false;
    closeMenu();
});

$(window).resize(function () {
	if ($(window).width() > 768) { menuWasOpen = false; closeMenu(); }
});

function closeMenu() {
	if (menuWasOpen) {
		$('body').addClass('menu--opened');
		$('.header--togg-menu, .header .morph-shape').addClass('open');
		$('.main-nav').addClass('open');
	} else {
		$('body').removeClass('menu--opened');
		$('.main-nav').removeClass('open');
		$('.main-nav .menu--link-has-dropdown .submenu').removeClass('opened');
		$('.header--togg-menu, .morph-shape').removeClass('open');
	}
}

// Open submenu on mobile tap
function openSubMenuOnTuch() {
	$('.header--navigation-menu-container').on('click', '.header--main-nav-mobile .menu--link-has-dropdown', function () {
		var get_data = $(this).data("submenu");
		var sub_menu = $(this).closest('.header--main-nav-mobile .main-nav--menu').find('#' + get_data);
		$(sub_menu).addClass('opened');
	});
}
openSubMenuOnTuch();

// Navigate back to parent menu level on mobile
function goToPrevMenuOnTuch() {
	$('.header--navigation-menu-container ').on('click', '.header--main-nav-mobile .submenu .to-previous-step', function (e) {
		$('.submenu').removeClass('opened');
		e.stopPropagation();
	});
}
goToPrevMenuOnTuch();

initSimpleTabs($('.tab-switcher--header:not(.developer-tab-switch):not(.team-tab-switch)'));

// ─────────────────────────────────────────────────────────────────────────────
// HEADER ACCORDION RELOCATION — moves accordion items on mobile
//
// On < 768px: moves accordion panels adjacent to their trigger links.
// On >= 768px: moves them back into the main .accordion container.
// ─────────────────────────────────────────────────────────────────────────────

(function () {
	const BREAKPOINT = 768;

	function relocateAccordionItems() {
		const accordionContainer = document.querySelector('.sb-header .accordion');
		const headerItems = document.querySelectorAll('.accordion-header__item [data-accordion]');
		if (!accordionContainer || !headerItems.length) return;

		if (window.innerWidth < BREAKPOINT) {
			headerItems.forEach(link => {
				const targetId = link.getAttribute('data-accordion');
				const targetBlock = document.querySelector(targetId);
				if (!targetBlock) return;
				const parentItem = link.closest('.accordion-header__item');
				const next = parentItem.nextElementSibling;
				if (!next || !next.isSameNode(targetBlock)) parentItem.after(targetBlock);
			});
		} else {
			document.querySelectorAll('.sb-header .accordion-item').forEach(item => {
				if (!accordionContainer.contains(item)) accordionContainer.appendChild(item);
			});
		}
	}

	document.addEventListener('DOMContentLoaded', relocateAccordionItems);

	let resizeTimer;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(relocateAccordionItems, 150);
	});

	// Re-run if header HTML is injected late; disconnect once accordion is found
	const relocationObserver = new MutationObserver(() => {
		if (document.querySelector('.sb-header .accordion')) {
			relocateAccordionItems();
			relocationObserver.disconnect();
		}
	});
	relocationObserver.observe(document.body, { childList: true, subtree: true });
})();

// ─────────────────────────────────────────────────────────────────────────────
// ANCHOR SCROLL & SECTION HIGHLIGHTING
//
// Handles smooth scroll on .anchor-link clicks with correct offset for the
// sticky header. Also highlights the active anchor link as sections scroll
// into view via IntersectionObserver.
// ─────────────────────────────────────────────────────────────────────────────

function smoothScrollAnchorLinks() {
	$('.anchor-link').click(function (e) {
		e.preventDefault();
		$('.anchor-link').removeClass('active');

		var target = $($(this).attr('href'));
		if (!target.length) return;

		var offset_value = $(this).data('offset') || 0;
		if (getMode() != 'desktop') offset_value = offset_value - $(this).height();
		if (getMode() == 'mob') offset_value = 0;

		var scrollTo = target.offset().top - (offset_value > 0 ? offset_value : 0);
		var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		var duration = prefersReduced ? 0 : 800;
		var scrollDone = false;

		$('body, html').stop().animate({ scrollTop: scrollTo }, duration, function () {
			if (scrollDone) return;
			scrollDone = true;
			var targetEl = target[0];
			var focusable = target.find('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])').filter(':visible').first();
			if (focusable.length) {
				focusable[0].focus({ preventScroll: true });
			} else {
				if (!targetEl.hasAttribute('tabindex')) targetEl.setAttribute('tabindex', '-1');
				targetEl.focus({ preventScroll: true });
			}
		});
	});

	var anchorLinks = document.querySelectorAll('.anchor-link');
	if (!anchorLinks.length) return;

	var targetLinkMap = {};
	anchorLinks.forEach(function (link) {
		var href = link.getAttribute('href');
		if (!href || href.charAt(0) !== '#') return;
		var id = href.slice(1);
		if (!targetLinkMap[id]) targetLinkMap[id] = [];
		targetLinkMap[id].push(link);
	});

	var targetEls = Object.keys(targetLinkMap).map(function (id) { return document.getElementById(id); }).filter(Boolean);
	if (!targetEls.length) return;

	// rootMargin: -90px top (sticky header height), -60% bottom (only upper viewport triggers)
	var sectionObserver = new IntersectionObserver(function (entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return;
			anchorLinks.forEach(function (link) { link.classList.remove('--active'); });
			if (targetLinkMap[entry.target.id]) {
				targetLinkMap[entry.target.id].forEach(function (link) { link.classList.add('--active'); });
			}
		});
	}, { rootMargin: '-90px 0px -60% 0px', threshold: 0 });

	targetEls.forEach(function (el) { sectionObserver.observe(el); });
}
smoothScrollAnchorLinks();

// ─────────────────────────────────────────────────────────────────────────────
// SCROLL-BASED VISIBILITY UTILITY
//
// Tracks whether an element is in the viewport and fires a callback on enter/leave.
// Used for map animations, integrations nav visibility, etc.
// ─────────────────────────────────────────────────────────────────────────────

function scrollToBlock(cur_block, callback, additionalOffset) {
	if ($(cur_block).length == 0) return;

	var isIn = checkIsIN();
	if (callback) callback(isIn);

	function checkIsIN() {
		var hT = cur_block.offset().top,
			hH = cur_block.outerHeight(),
			wH = $(window).height(),
			wS = additionalOffset ? $(window).scrollTop() + additionalOffset : $(window).scrollTop();
		return wS > (hT + hH - wH) && (hT > wS) && (wS + wH > hT + hH);
	}

	document.addEventListener('scroll', function () {
		if (checkIsIN()) {
			cur_block.addClass('in');
			if (!isIn) { if (callback) callback(true); isIn = true; }
		} else {
			cur_block.removeClass('in');
			if (isIn) { if (callback) callback(false); isIn = false; }
		}
	}, { passive: true });
}

scrollToBlock($('#letter-block'));

// @moved — integrations page hero-section nav visibility now lives in
// assets/js/pages/integrationsPage.js (only that page has #hero-section /
// .integrations__fixed-nav).

// Covid page — only scroll-track on tablet/desktop
if (getMode() == 'tablet' || getMode() == 'desktop') {
	scrollToBlock($('#covid-letter-block'), '', 150);
}

// @deprecated — number counter via scrollToBlock replaced by IntersectionObserver below (initNumberCounters)
// scrollToBlock($('.partnership__key-numbers_list'), function (isIn){ if(isIn){ numCounter(); } })

// ─────────────────────────────────────────────────────────────────────────────
// SCROLL TO TOP BUTTON
// ─────────────────────────────────────────────────────────────────────────────

const $scrollToTopBtn = $('#scroll-to-top');
document.addEventListener('scroll', function () {
	if ($(window).scrollTop() >= 200) $scrollToTopBtn.addClass('in');
	else $scrollToTopBtn.removeClass('in');
}, { passive: true });

$scrollToTopBtn.click(function (e) {
	e.preventDefault();
	$('body,html').animate({ scrollTop: 0 }, 500);
});

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATED NUMBER COUNTERS
//
// Replaces the old jQuery-animate numCounter() approach.
// Uses IntersectionObserver + requestAnimationFrame for smooth, accessible animation.
// Resets on leave so re-entering the viewport replays the animation.
// ─────────────────────────────────────────────────────────────────────────────

function initNumberCounters() {
	const counters = document.querySelectorAll('.count-value');
	if (!counters.length) return;

	const animateValue = (el, start, end, duration, decimals, suffix = '') => {
		const startTime = performance.now();
		const tick = (now) => {
			const progress = Math.min((now - startTime) / duration, 1);
			el.textContent = (start + (end - start) * progress).toFixed(decimals) + suffix;
			if (progress < 1) requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
	};

	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			const el = entry.target;
			const dataVal = el.dataset.val;
			if (!dataVal) return;
			const hasPercent = dataVal.includes('%');
			const cleanVal = parseFloat(dataVal.replace(/[,%]/g, '')) || 0;
			const decimals = (cleanVal.toString().split('.')[1] || '').length;
			const suffix = hasPercent ? '%' : '';
			if (entry.isIntersecting) animateValue(el, 0, cleanVal, 2600, decimals, suffix);
			else el.textContent = '0' + suffix; // reset so re-entry replays
		});
	}, { threshold: 0.17 });

	counters.forEach(el => observer.observe(el));
}
initNumberCounters();

// ─────────────────────────────────────────────────────────────────────────────
// REVIEW ACCORDION — expand/collapse long review text inside the slider
// ─────────────────────────────────────────────────────────────────────────────

function reviewAccordion() {
	const slider = $('.reviews--slider');
	if (!slider.length) return;

	// Auto-assign IDs for ARIA
	slider.find('.short-review').each(function (index) {
		const id = 'review-text-' + (index + 1);
		$(this).attr('id', id);
		$(this).find('.read-more').attr('aria-controls', id);
	});

	slider.on('click keydown', '.read-more', function (e) {
		if (e.type === 'keydown' && !(e.key === 'Enter' || e.key === ' ')) return;

		const btn = $(this);
		const review = btn.closest('.short-review');
		const isOpen = btn.hasClass('open');
		const sliderEl = $('.reviews--slider');

		e.preventDefault();
		btn.attr('aria-expanded', !isOpen);
		btn.toggleClass('open');
		review.toggleClass('full-review');

		// Collapse all others
		slider.find('.read-more').not(btn).removeClass('open').attr('aria-expanded', 'false');
		slider.find('.short-review').not(review).removeClass('full-review').css('max-height', 76);

		// Animate expand/collapse; notify slick to recalculate slide heights
		var slickReady = sliderEl.hasClass('slick-initialized');
		if (review.hasClass('full-review')) {
			review.animate({ 'max-height': review[0].scrollHeight + "px" }, {
				duration: 300,
				step: function () {
					if (slickReady) { sliderEl.slick('resize'); sliderEl.slick("setOption", null, null, true); }
				}
			});
		} else {
			review.animate({ 'max-height': 76 }, {
				duration: 200,
				step: function () {
					if (slickReady) { sliderEl.slick('resize'); sliderEl.slick("setOption", null, null, true); }
				}
			});
		}

		// Deferred resize helpers to handle font-rendering lag
		[50, 150, 350].forEach(delay => setTimeout(resizeReviewsCarousel, delay));

		function resizeReviewsCarousel() {
			if (!sliderEl.hasClass('slick-initialized')) return;
			sliderEl.slick('resize');
			sliderEl.find(".slick-slide").css("height", "auto");
			sliderEl.find(".slick-list").css("height", "auto");
		}
	});
}
reviewAccordion();

// ─────────────────────────────────────────────────────────────────────────────
// BOOKING TEMPLATES — theme selection persistence
//
// When a user selects a theme on the main page and is redirected to the
// Booking Templates page, the chosen theme ID is read from localStorage and
// auto-scrolled to + activated.
// ─────────────────────────────────────────────────────────────────────────────

let themesList = $('#templates .templates-list__item .btn--select-theme');
if ($(themesList).length) {
	themesList.on('click', function () {
		localStorage.setItem('selectedTheme', $(this).data('theme-id'));
	});
}

var localStorageData = localStorage.getItem('selectedTheme');
if ($('.templates-items--section').length) {
	let items = $('.templates-items--section .templates-items--list-item');
	var selectedButton = items.filter(function () {
		return $(this).find('.anchor-link').data('theme-id') == localStorageData;
	}).find('.anchor-link');

	$(document).ready(function () {
		setTimeout(function () {
			selectedButton.trigger('click');
			localStorage.removeItem('selectedTheme');
		}, 1500);
	});
}

// @deprecated — mouse-leave exit intent popup, disabled due to poor UX metrics
// $(document).bind('mouseleave', function (e) { ... });

// ─────────────────────────────────────────────────────────────────────────────
// ENTERPRISE WIDGET TRIGGER — proxy click from .widget_btn to #sb_enterprise_widget
// ─────────────────────────────────────────────────────────────────────────────

if ($(".widget_btn").length) {
	$(".widget_btn").click(function () {
		$("#sb_enterprise_widget").trigger("click");
	});
}

// ─────────────────────────────────────────────────────────────────────────────
// DOM-READY COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

$(document).ready(function () {

	// Dev environment cookie banner suppression (local dev URLs only)
	if (window.location.href.indexOf("igor.d") > -1 || window.location.href.indexOf("memosh.dev18") > -1) {
		// $(.sb-important').fadeOut();
	}

	// FAQ accordion — accessible expand/collapse for .faq-item elements
	function faqAccordionInit() {
		const toggles = document.querySelectorAll('.faq-item__toggler');
		toggles.forEach(toggle => {
			const controlledId = toggle.getAttribute('aria-controls');
			const content = document.getElementById(controlledId);

			// Plain max-height transition (mirrors accordion.js) instead of jQuery
			// slideDown/slideUp, so opening/closing never depends on jQuery's own
			// reflow timing interacting with content-visibility/beforematch.
			// `instant` skips the height animation and jumps straight to the fully-open
			// state — used for the beforematch/Find-in-Page reveal path, where the browser
			// calls its own scrollIntoView() synchronously right after the handler returns;
			// an animation still in flight at that point scrolls to a layout that's about
			// to change underneath it (landing short, or cropped under the sticky header).
			const openFAQ = (instant = false) => {
				if (content.classList.contains('expanded')) return;
				toggle.setAttribute('aria-expanded', 'true');
				toggle.classList.add('--active');
				content.removeAttribute('hidden');
				content.setAttribute('aria-hidden', 'false');
				content.classList.add('expanded');
				if (instant) {
					content.style.maxHeight = 'none';
				} else {
					requestAnimationFrame(() => {
						if (content.classList.contains('expanded')) {
							content.style.maxHeight = content.scrollHeight + 'px';
						}
					});
				}
			};

			const closeFAQ = () => {
				if (!content.classList.contains('expanded')) return;
				const collapse = () => {
					content.classList.remove('expanded');
					content.style.maxHeight = '0px';
				};
				// A fully-open panel has max-height released to "none" (see the transitionend
				// listener below) — CSS can't transition away from "none", so restore the exact
				// pixel height first (while .expanded/padding is still applied). A single rAF
				// after that still lands in the same unpainted frame as this synchronous write,
				// so a second rAF is needed to force a real paint of the restored height first —
				// otherwise the collapse to 0 is an unanimatable jump instead of a transition.
				if (content.style.maxHeight === 'none') {
					content.style.maxHeight = content.scrollHeight + 'px';
					requestAnimationFrame(() => requestAnimationFrame(collapse));
				} else {
					requestAnimationFrame(collapse);
				}
				toggle.setAttribute('aria-expanded', 'false');
				toggle.classList.remove('--active');
				content.setAttribute('aria-hidden', 'true');
				content.setAttribute('hidden', 'until-found');
			};

			const toggleFAQ = () => {
				if (content.classList.contains('expanded')) {
					closeFAQ();
				} else {
					openFAQ();
				}
			};

			toggle.addEventListener('click', e => { e.preventDefault(); toggleFAQ(); });
			toggle.addEventListener('keydown', e => {
				if (['Enter', ' '].includes(e.key)) { e.preventDefault(); toggleFAQ(); }
			});

			// Browser find-in-page reveals hidden="until-found" content on its own;
			// this just syncs our aria/--active state and reveals it instantly (see openFAQ).
			content.addEventListener('beforematch', () => openFAQ(true));

			// Once the open transition finishes, release the height cap entirely so later
			// content/viewport changes are never clipped by a stale (or guessed) max-height.
			content.addEventListener('transitionend', e => {
				if (e.propertyName === 'max-height' && content.classList.contains('expanded')) {
					content.style.maxHeight = 'none';
				}
			});

			const header = toggle.closest('.faq-item__header');
			if (header) {
				header.addEventListener('click', e => {
					if (!toggle.contains(e.target)) {
						e.preventDefault();
						toggleFAQ();
					}
				});
			}
		});
	}
	faqAccordionInit();

	// Key benefits collapsible section — shows first 2 items, expand on click
	function toggleKeyBenefitsView() {
		const $section = $('.key-benefits.collapsible');
		if (!$section.length) return;

		const $items = $section.find('.key-benefits__col');
		const $toggler = $section.find('.key-benefits__col-toggler');
		const $toggleBtn = $toggler.find('.toggle-view-items');
		const itemsToShowInitially = 2;

		if ($items.length > itemsToShowInitially) {
			$items.each(function (index) {
				if (index <= itemsToShowInitially) $(this).addClass('key-benefits__col--visible');
				else $(this).hide().addClass('key-benefits__col--hidden');
			});
			$toggler.removeClass('hidden');

			$toggleBtn.on('click', function () {
				const isExpanded = $section.hasClass('key-benefits--expanded');
				$(this).toggleClass('expanded');
				if (isExpanded) {
					$items.each(function (index) { if (index > itemsToShowInitially) $(this).slideUp(); });
					$section.removeClass('key-benefits--expanded');
				} else {
					$items.slideDown();
					$section.addClass('key-benefits--expanded');
				}
			});
		}
	}
	toggleKeyBenefitsView();

	// @disabled — shiny button shimmer effect, disabled pending design decision
	// startRandomShine();

	// @deprecated — Swiper prototype, replaced by lazyInitializer slick presets
	// const swiper = new Swiper('.swiper', { ... });
});

// ─────────────────────────────────────────────────────────────────────────────
// ACCESSIBILITY COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// Keyboard navigation for custom .tooltip-component elements
function tooltipKeyboardNav() {
	const tooltips = document.querySelectorAll('.tooltip-component');
	if (!tooltips.length) return;

	tooltips.forEach(el => {
		el.setAttribute('tabindex', '0');
		el.addEventListener('keydown', e => {
			if (['Enter', ' '].includes(e.key)) { e.preventDefault(); el.classList.toggle('is-active'); }
		});
		el.addEventListener('focus', () => el.classList.add('is-active'));
		el.addEventListener('blur', () => el.classList.remove('is-active'));
		el.addEventListener('mouseleave', () => el.classList.remove('is-active'));
	});
}

/**
 * Unified dropdown control — supports hover, click, and full keyboard navigation.
 * Keeps open/close state consistent across mouse and keyboard input methods.
 */
function mainMenuDropdownKeyboardNav() {
	const dropdowns = document.querySelectorAll('.dropdown');
	if (!dropdowns.length) return;

	dropdowns.forEach(dropdown => {
		const toggle = dropdown.querySelector('.dropdown-toggle, [data-dropdown]');
		const menu = dropdown.querySelector('.dropdown-menu');
		if (!toggle || !menu) return;

		const items = menu.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
		let isOpen = false;
		let isMouseInside = false;

		const openMenu = () => {
			if (isOpen) return;
			menu.classList.add('is-visible');
			toggle.setAttribute('aria-expanded', 'true');
			isOpen = true;
			items.forEach(el => el.setAttribute('tabindex', '0'));
		};

		const closeMenu = () => {
			if (!isOpen) return;
			menu.classList.remove('is-visible');
			toggle.setAttribute('aria-expanded', 'false');
			isOpen = false;
			items.forEach(el => el.setAttribute('tabindex', '-1'));
		};

		toggle.addEventListener('click', e => {
			if (!isOpen && toggle.tagName.toLowerCase() === 'a') e.preventDefault();
			isOpen ? closeMenu() : openMenu();
		});

		toggle.addEventListener('keydown', e => {
			if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
				e.preventDefault();
				openMenu();
				setTimeout(() => {
					if (e.key === 'ArrowUp') items[items.length - 1]?.focus();
					else items[0]?.focus();
				}, 80);
			}
		});

		menu.addEventListener('keydown', e => {
			const list = Array.from(items);
			const i = list.indexOf(document.activeElement);
			switch (e.key) {
				case 'ArrowDown': e.preventDefault(); list[(i + 1) % list.length]?.focus(); break;
				case 'ArrowUp':   e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); break;
				case 'Home':      e.preventDefault(); list[0]?.focus(); break;
				case 'End':       e.preventDefault(); list[list.length - 1]?.focus(); break;
				case 'Escape':    e.preventDefault(); closeMenu(); toggle.focus(); break;
			}
		});

		dropdown.addEventListener('mouseenter', () => { isMouseInside = true; openMenu(); });
		dropdown.addEventListener('mouseleave', () => {
			isMouseInside = false;
			setTimeout(() => { if (!isMouseInside && !dropdown.contains(document.activeElement)) closeMenu(); }, 120);
		});

		document.addEventListener('click', e => { if (!dropdown.contains(e.target)) closeMenu(); });

		// Wait a tick so the next focused element is known before closing
		dropdown.addEventListener('focusout', function () {
			setTimeout(function () {
				if (!dropdown.contains(document.activeElement) && !isMouseInside) closeMenu();
			}, 100);
		});

		items.forEach(el => el.setAttribute('tabindex', '-1'));
	});
}
/**
 * Mirrors the compare-table--embed.html.twig horizontal scroll in the thin bar
 * underneath it. Purely an indicator: aria-hidden, not draggable. Shared across
 * every page using the component so no per-page JS entry is needed (CY1SDEV1-4410).
 */
function compareTableScrollIndicator() {
	document.querySelectorAll('[data-compare-scroll]').forEach(scroller => {
		const wrapper = scroller.closest('.compare-table-v2');
		const track = wrapper && wrapper.querySelector('[data-compare-indicator]');
		const thumb = wrapper && wrapper.querySelector('[data-compare-thumb]');

		function update() {
			const scrollable = scroller.scrollWidth - scroller.clientWidth;

			// Drives the pinned feature column's edge shadow (CY1SDEV1-4410
			// follow-up): only a real affordance when there's something to
			// scroll to, e.g. not on setmore-alternative where the table now
			// fills its container exactly.
			if (wrapper) {
				wrapper.classList.toggle('is-scrollable', scrollable > 0);
			}

			if (!track || !thumb) return;
			if (scrollable <= 0) {
				track.style.visibility = 'hidden';
				return;
			}
			track.style.visibility = '';

			const ratio = scroller.clientWidth / scroller.scrollWidth;
			const thumbWidth = Math.max(ratio * 100, 10);
			thumb.style.width = thumbWidth + '%';
			thumb.style.transform =
				'translateX(' + (scroller.scrollLeft / scrollable) * ((100 - thumbWidth) / thumbWidth) * 100 + '%)';
		}

		scroller.addEventListener('scroll', update, { passive: true });
		window.addEventListener('resize', update);
		update();
	});
}

onDOMReady(() => {
	runWhenReady('.tooltip-component', tooltipKeyboardNav);
	runWhenReady('.dropdown', mainMenuDropdownKeyboardNav);
	runWhenReady('[data-compare-scroll]', compareTableScrollIndicator);
});
